import { useEffect, useRef, useState } from "react";
import { useT } from "../../../../i18n/I18nProvider";
import { cardOrderPageLink, getCardOrder, remakeCardOrderFile, type CardOrderRow as Row } from "../../../../lib/cardOrders";
import { useToast } from "../../../ui";

/** How often the list looks for the file while the studio page draws it, and for how long. */
const LOOK_EVERY_MS = 4000;
const LOOK_FOR_MS = 5 * 60_000;

/**
 * Making a card order's print file from the CRM.
 *
 * The server cannot draw a print file: the card studio's page does, in a browser. So both
 * steps here end in the same place, a new tab with the studio page and the order in it
 * (`cardOrderPageLink`), where the file is drawn and handed in:
 *
 *  - "Make a new file" (`make`), for a paid order that keeps none: the tab opens.
 *  - "Make the print file again" (`ask`, then `confirm`), for an order that keeps one: a
 *    question first, then the kept file is deleted, then the tab opens.
 *
 * The tab is opened inside the click and sent to its address when the server has answered.
 * A tab opened after the answer is not the click's any more, and browsers block it (the
 * client view of a project opens the same way, `projects/useClientPreview.ts`). Where the
 * browser gives no tab at all, nothing is deleted: a deleted file with no way to a new one
 * is the state this step exists to get out of.
 *
 * After the tab is on its way the list looks for the file (every few seconds while this
 * tab shows, and when this tab shows again), so the order's row and window get the new
 * file without a reload. `onOrder` is handed every order as it reads now.
 */
export function useFileMaking(onOrder: (order: Row) => void) {
  const t = useT();
  const toast = useToast();
  // The order the question is about. Kept while the question fades out, so its text does
  // not change under the reader
  const [asked, setAsked] = useState<Row | null>(null);
  const [asking, setAsking] = useState(false);
  /** The order whose step is on its way to the server. */
  const [busy, setBusy] = useState<string | null>(null);
  /** The order whose file the studio page is drawing. */
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const latest = useRef(onOrder);
  latest.current = onOrder;
  // One step at a time. A ref, because two fast clicks see the same render's state
  const running = useRef(false);

  useEffect(() => {
    if (!awaiting) return;
    let live = true;
    const look = () => {
      if (document.visibilityState !== "visible") return;
      getCardOrder(awaiting)
        .then((order) => {
          if (!live || !order.hasFile) return;
          latest.current(order);
          toast(t("sales.cardOrders.fileArrived"));
          setAwaiting(null);
        })
        .catch(() => {
          /* the next look tries again */
        });
    };
    const timer = setInterval(look, LOOK_EVERY_MS);
    const stop = setTimeout(() => setAwaiting(null), LOOK_FOR_MS);
    document.addEventListener("visibilitychange", look);
    return () => {
      live = false;
      clearInterval(timer);
      clearTimeout(stop);
      document.removeEventListener("visibilitychange", look);
    };
  }, [awaiting, toast, t]);

  /** Send the tab to the studio page of the order. False when the page could not be had. */
  async function toPage(order: Row, tab: Window): Promise<boolean> {
    try {
      const { url } = await cardOrderPageLink(order.id);
      // Before the page loads, so that it never has a way back into the CRM
      tab.opener = null;
      tab.location.href = url;
      setAwaiting(order.id);
      return true;
    } catch {
      tab.close();
      return false;
    }
  }

  async function make(order: Row) {
    if (running.current) return;
    const tab = window.open("", "_blank");
    if (!tab) {
      toast(t("sales.cardOrders.popupBlocked"));
      return;
    }
    running.current = true;
    setBusy(order.id);
    try {
      toast(t((await toPage(order, tab)) ? "sales.cardOrders.makeStarted" : "sales.cardOrders.makeFailed"));
    } finally {
      running.current = false;
      setBusy(null);
    }
  }

  function ask(order: Row) {
    setAsked(order);
    setAsking(true);
  }

  // The order's window comes back when the question goes. It listens for Escape too, and
  // the press that cancelled the question is still on its way to the window then: so the
  // question goes only after that press is over
  function cancel() {
    setTimeout(() => setAsking(false), 0);
  }

  async function confirm() {
    const order = asked;
    if (!order || running.current) return;
    const tab = window.open("", "_blank");
    if (!tab) {
      toast(t("sales.cardOrders.popupBlocked"));
      return;
    }
    running.current = true;
    setBusy(order.id);
    try {
      let left = false;
      try {
        const res = await remakeCardOrderFile(order.id);
        left = res.leftInStorage;
        latest.current(res.order);
      } catch {
        // Nothing was deleted. The question stays, so the same press can try again
        tab.close();
        toast(t("sales.cardOrders.remakeFailed"));
        return;
      }
      setAsking(false);
      const opened = await toPage(order, tab);
      // One line at a time: the one the reader has to act on wins
      if (!opened) toast(t("sales.cardOrders.remakeNoPage"));
      else toast(t(left ? "sales.cardOrders.remakeLeft" : "sales.cardOrders.remakeDone"));
    } finally {
      running.current = false;
      setBusy(null);
    }
  }

  return { asked, asking, busy, ask, cancel, confirm, make };
}
